import React, {useEffect, useState} from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux'
import { getToken } from '../../../../../features/token/tokenSaver';
import './AdvertiserCampaignDetails.css'
import { deleteAdvertisementCampaignAdmin } from '../../../../../services/deleteAdvertismentCampaignAdmin';
import { getCampaignDetailsAdmin } from '../../../../../services/getCampaignDetailsAdmin';
import leftChevron from '../../../../../assets/images/leftChevron.png';
import { approveAdvertisement } from '../../../../../services/approveAdvertisement';
import { toggleAdvertisment } from '../../../../../services/toggleAdvertisement';
import { adminUpdateCampaignContent } from '../../../../../services/adminUpdateCampaignContent';
import { adminApproveCampaignContent } from '../../../../../services/adminApproveCampaignContent';
import { adminRejectCampaignContent } from '../../../../../services/adminRejectCampaignContent';
import { adminUpdateCampaignScreens } from '../../../../../services/adminUpdateCampaignScreens';
import { useCampaignCreativeUpload } from '../../../../../hooks/useCampaignCreativeUpload';
import CampaignContentUploadForm from '../../../../../components/CampaignContentUploadForm/CampaignContentUploadForm';
import CampaignCreativePreview from '../../../../../components/CampaignCreativePreview/CampaignCreativePreview';
import ScreenSelector from '../../../../Advertiser/AdvertiserDashboard/ScreenSelector/ScreenSelector';
import { totalPlaysForScreens, PLAYS_PER_SCREEN_MONTH } from '../../../../../utils/advertiserScreenPricing';
import {
    formatAdminCampaignPaymentStatus,
    isUnpaidCampaignStatus,
} from '../../../../../utils/adminCampaignPaymentStatus';
import {
    getOutreachTemplates,
    sendOutreachReminder,
    previewOutreachTemplate,
} from '../../../../../services/advertiserOutreach';

const ADMIN_CONTENT_INPUT_IDS = {
    horizontal: 'adminCampaignContentHorizontal',
    vertical: 'adminCampaignContentVertical',
};

export default function AdminDashboardAdvertiserCampaignDetails() {
    const { state } = useLocation();
    const navigate = useNavigate();
    const token = useSelector(getToken)
    const [adData, setAdData] = useState({});
    const [targetedScreens, setTargetedScreens] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [reminderOpen, setReminderOpen] = useState(false);
    const [outreachTemplates, setOutreachTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [reminderChannels, setReminderChannels] = useState({ email: true, sms: false });
    const [selectedTemplateKey, setSelectedTemplateKey] = useState('');
    const [useCustomMessage, setUseCustomMessage] = useState(false);
    const [customEmailSubject, setCustomEmailSubject] = useState('');
    const [customEmailHtml, setCustomEmailHtml] = useState('');
    const [customSmsBody, setCustomSmsBody] = useState('');
    const [reminderSendError, setReminderSendError] = useState('');
    const [reminderSendSuccess, setReminderSendSuccess] = useState('');
    const [showEditContent, setShowEditContent] = useState(false);
    const [contentActionError, setContentActionError] = useState('');
    const [contentSubmitting, setContentSubmitting] = useState(false);
    const [showEditScreens, setShowEditScreens] = useState(false);
    const [editScreenIds, setEditScreenIds] = useState([]);
    const [editFeeInput, setEditFeeInput] = useState('');
    const [screenSaveError, setScreenSaveError] = useState('');
    const [screenSaving, setScreenSaving] = useState(false);
    const adminContentUpload = useCampaignCreativeUpload(ADMIN_CONTENT_INPUT_IDS);
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    });

    function loadCampaignDetails() {
        setLoading(true);
        setLoadError('');

        getCampaignDetailsAdmin(token, state?.adId).then((campaignResponse) => {
            const campaignData = campaignResponse.data?.advertismentData || campaignResponse.data?.campaign || {};
            setAdData(campaignData);
            const screens = campaignResponse.data?.targetedScreens || [];
            setTargetedScreens(screens);
            setEditScreenIds(screens);
            const fee = campaignData.MonthlyFee;
            setEditFeeInput(Number.isFinite(Number(fee)) && Number(fee) > 0 ? Number(fee).toFixed(2) : '');
        }).catch(() => {
            setLoadError('Unable to load campaign details right now. Please try again.');
        }).finally(() => {
            setLoading(false);
        });
    }

    useEffect(() => {
        if (!state?.adId) {
            navigate(-1)
            return;
        }
        loadCampaignDetails();

    }, [state?.adId, token]);

    function formatDate(value) {
        if (!value) return 'N/A';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return 'N/A';
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    }

    function formatCurrency(value) {
        if (value === null || value === undefined || value === '') return 'N/A';
        return formatter.format(Number(value));
    }

    function getStatusLabel(statusId) {
        if (statusId === 1) return 'Active';
        if (statusId === 2) return 'Inactive';
        if (statusId === 3) return 'Pending Approval';
        return 'Unknown';
    }

    function getTargetingLabel() {
        const mode = adData.TargetingMode || adData.targetingMode || 'network';
        if (mode === 'selected_screens' && targetedScreens.length > 0) {
            return `Screen #${targetedScreens.join(', #')}`;
        }
        if (mode === 'selected_screens') {
            return 'Selected screens';
        }
        return 'All screens';
    }

    function approveAdvertisementCall() {
        setActionLoading(true)
        approveAdvertisement({
            adId: adData.AdvertisementId,
        }, token).then(() => {
            alert("Campaign approved.");
            loadCampaignDetails();
        }).catch(() => {
            alert('Unable to approve campaign right now.');
        }).finally(() => {
            setActionLoading(false)
        })
    }

    function hasPendingContentRevision() {
        return Boolean(adData.PendingAdContentHorizontal && adData.PendingAdContentVertical);
    }

    function isPendingApprovalCampaign() {
        return String(adData.SubscriptionStatus || '').toLowerCase() === 'pending_approval';
    }

    async function handleAdminReplaceContent(payload) {
        setContentActionError('');
        setContentSubmitting(true);
        try {
            await adminUpdateCampaignContent(adData.AdvertisementId, payload, token);
            alert('Campaign creatives updated.');
            setShowEditContent(false);
            adminContentUpload.resetCreatives();
            loadCampaignDetails();
        } catch (err) {
            setContentActionError(err.response?.data?.error || 'Unable to update creatives.');
        } finally {
            setContentSubmitting(false);
        }
    }

    async function handleApproveContentRevision() {
        if (!window.confirm('Approve these new creatives? They will replace the current live assets.')) {
            return;
        }
        setContentActionError('');
        setActionLoading(true);
        try {
            await adminApproveCampaignContent(adData.AdvertisementId, token);
            alert('Creative revision approved.');
            loadCampaignDetails();
        } catch (err) {
            alert(err.response?.data?.error || 'Unable to approve creative revision.');
        } finally {
            setActionLoading(false);
        }
    }

    function handleEditScreenSelectionChange(selection) {
        setEditScreenIds(selection.screenIds);
        setEditFeeInput(selection.totalUsd > 0 ? selection.totalUsd.toFixed(2) : '');
        setScreenSaveError('');
    }

    async function handleSaveCampaignScreens() {
        if (!editScreenIds.length) {
            setScreenSaveError('Select at least one screen.');
            return;
        }
        setScreenSaveError('');
        setScreenSaving(true);
        try {
            const fee = Number(editFeeInput);
            const payload = { screenIds: editScreenIds };
            if (Number.isFinite(fee) && fee > 0) {
                payload.monthlyFee = fee;
            }
            await adminUpdateCampaignScreens(adData.AdvertisementId, payload, token);
            alert('Campaign screens updated.');
            setShowEditScreens(false);
            loadCampaignDetails();
        } catch (err) {
            setScreenSaveError(err.response?.data?.error || 'Unable to update screens.');
        } finally {
            setScreenSaving(false);
        }
    }

    async function handleRejectContentRevision() {
        const denialReason = window.prompt('Optional note to send the advertiser (leave blank for none):') || '';
        setContentActionError('');
        setActionLoading(true);
        try {
            await adminRejectCampaignContent(adData.AdvertisementId, { denialReason }, token);
            alert('Creative revision rejected.');
            loadCampaignDetails();
        } catch (err) {
            alert(err.response?.data?.error || 'Unable to reject creative revision.');
        } finally {
            setActionLoading(false);
        }
    }

    function deleteAdvertisement() {
        setActionLoading(true)
        var confirmed = window.confirm(`Delete Advertisment #${adData.AdvertisementId}?`);
        if (!confirmed) {
            setActionLoading(false)
            return;
        }
        deleteAdvertisementCampaignAdmin(token, adData.AdvertisementId).then(() => {
            navigate(-1);
        }).finally(() => {
            setActionLoading(false)
        })
    }

    const SAMPLE_CONTINUE_LINK = 'https://invert.example/launch/continue?token=sample';

    function getReminderPreviewVars() {
        const amount = Number.isFinite(Number(adData.MonthlyFee))
            ? Number(adData.MonthlyFee).toFixed(2)
            : '0.00';
        return {
            continueLink: SAMPLE_CONTINUE_LINK,
            firstName: adData.AdvertiserFname || 'there',
            companyName: adData.AdvertiserCompanyName || '',
            campaignName: adData.AdvertisementName || 'Launch Campaign',
            amount,
        };
    }

    function openReminderModal() {
        setReminderOpen(true);
        setReminderSendError('');
        setReminderSendSuccess('');
        setTemplatesLoading(true);
        getOutreachTemplates(token, 'launch_unpaid')
            .then((res) => {
                const list = res.data?.templates || [];
                setOutreachTemplates(list);
                const emailDefault = list.find((t) => t.Channel === 'email' && t.IsDefault);
                const smsDefault = list.find((t) => t.Channel === 'sms' && t.IsDefault);
                setSelectedTemplateKey(
                    emailDefault?.TemplateKey || smsDefault?.TemplateKey || list[0]?.TemplateKey || ''
                );
            })
            .catch(() => {
                setReminderSendError('Could not load message templates.');
            })
            .finally(() => {
                setTemplatesLoading(false);
            });
    }

    function closeReminderModal() {
        setReminderOpen(false);
        setReminderSendError('');
        setReminderSendSuccess('');
    }

    function getSelectedChannelsList() {
        const list = [];
        if (reminderChannels.email) list.push('email');
        if (reminderChannels.sms) list.push('sms');
        return list;
    }

    function getTemplatesForSelectedChannels() {
        const channels = getSelectedChannelsList();
        if (channels.length === 0) {
            return outreachTemplates;
        }
        return outreachTemplates.filter((t) => channels.includes(t.Channel));
    }

    function getActiveTemplatePreview() {
        const vars = getReminderPreviewVars();
        if (useCustomMessage) {
            if (reminderChannels.email) {
                const subject = previewOutreachTemplate(
                    customEmailSubject || 'Complete your INVERT ad payment',
                    vars
                );
                const body = previewOutreachTemplate(
                    customEmailHtml
                        || '<p>Hi {{firstName}},</p><p>Your ad <strong>{{campaignName}}</strong> is ready once payment is complete (${{amount}}).</p><p><a href="{{continueLink}}">Continue checkout</a></p>',
                    vars
                );
                return { emailSubject: subject, emailBody: body, smsBody: previewOutreachTemplate(customSmsBody, vars) };
            }
            return {
                smsBody: previewOutreachTemplate(
                    customSmsBody
                        || 'Hi {{firstName}}, finish your INVERT ad ({{campaignName}}, ${{amount}}): {{continueLink}}',
                    vars
                ),
            };
        }

        const selected = outreachTemplates.find((t) => t.TemplateKey === selectedTemplateKey);
        const emailTmpl =
            selected?.Channel === 'email'
                ? selected
                : outreachTemplates.find((t) => t.Channel === 'email' && t.IsDefault);
        const smsTmpl =
            selected?.Channel === 'sms'
                ? selected
                : outreachTemplates.find((t) => t.Channel === 'sms' && t.IsDefault);

        return {
            ...(emailTmpl
                ? {
                    emailSubject: previewOutreachTemplate(emailTmpl.Subject || '', vars),
                    emailBody: previewOutreachTemplate(emailTmpl.Body || '', vars),
                }
                : {}),
            ...(smsTmpl
                ? { smsBody: previewOutreachTemplate(smsTmpl.Body || '', vars) }
                : {}),
        };
    }

    function sendPaymentReminder() {
        const channelList = getSelectedChannelsList();
        if (channelList.length === 0) {
            setReminderSendError('Select at least one channel (email or SMS).');
            return;
        }
        if (channelList.includes('email') && !adData.AdvertiserEmail) {
            setReminderSendError('No advertiser email on file.');
            return;
        }
        if (channelList.includes('sms') && !adData.AdvertiserPhoneNumber) {
            setReminderSendError('No advertiser phone number on file.');
            return;
        }

        setActionLoading(true);
        setReminderSendError('');
        setReminderSendSuccess('');

        const payload = {
            advertisementId: adData.AdvertisementId,
            channels: channelList,
            audienceType: 'launch_unpaid',
        };

        if (!useCustomMessage && selectedTemplateKey) {
            payload.templateKey = selectedTemplateKey;
        }
        if (useCustomMessage) {
            if (channelList.includes('email')) {
                payload.customEmailSubject = customEmailSubject;
                payload.customEmailHtml = customEmailHtml;
            }
            if (channelList.includes('sms')) {
                payload.customSmsBody = customSmsBody;
            }
        }

        sendOutreachReminder(token, payload)
            .then(() => {
                setReminderSendSuccess('Payment reminder sent.');
                setTimeout(() => {
                    closeReminderModal();
                }, 1200);
            })
            .catch((err) => {
                const msg = err.response?.data?.error || err.message || 'Failed to send reminder.';
                setReminderSendError(msg);
            })
            .finally(() => {
                setActionLoading(false);
            });
    }

    function toggleCampaignStatus() {
        const currentStatus = adData.StatusId;
        const newStatus = currentStatus === 1 ? 2 : 1;
        const statusLabel = newStatus === 1 ? 'Active' : 'Inactive';
        
        const confirmed = window.confirm(
            `Change campaign status to ${statusLabel}?`
        );
        
        if (!confirmed) {
            return;
        }

        setActionLoading(true);
        toggleAdvertisment(token, adData.AdvertisementId, newStatus)
            .then(() => {
                alert(`Campaign status changed to ${statusLabel}.`);
                loadCampaignDetails();
            })
            .catch(() => {
                alert('Unable to toggle campaign status right now.');
            })
            .finally(() => {
                setActionLoading(false);
            });
    }

    if (loading) {
        return (
            <div className='adminCampaignModerationContainer'>
                <div className='campaignDetailsStateCard'>Loading campaign details...</div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className='adminCampaignModerationContainer'>
                <div className='campaignDetailsStateCard error'>{loadError}</div>
            </div>
        );
    }

  return (
      <div className='adminCampaignModerationContainer'>
          <img className='leftChevronAdvertiserDetails' onClick={() => { navigate(-1) }} src={leftChevron} />

              <h1 className='advertismentCampaignDetailsTitle'>Campaign Moderation</h1>

              <div className='campaignDetailsHeaderCard'>
                  <div>
                      <div className='campaignName'>{adData.AdvertisementName || 'Untitled Campaign'}</div>
                      <div className='campaignMeta'>Campaign #{adData.AdvertisementId}</div>
                  </div>
                  <span className={`campaignStatusBadge status-${adData.StatusId || 0}`}>
                      {getStatusLabel(adData.StatusId)}
                  </span>
              </div>

              <div className='campaignSummaryGrid'>
                  <div className='campaignSummaryCard'>
                      <span className='summaryLabel'>Targeting</span>
                      <span className='summaryValue'>{getTargetingLabel()}</span>
                  </div>
                  <div className='campaignSummaryCard'>
                      <span className='summaryLabel'>Monthly Fee</span>
                      <span className='summaryValue'>{formatCurrency(adData.MonthlyFee)}</span>
                  </div>
                  <div className='campaignSummaryCard'>
                      <span className='summaryLabel'>Payment</span>
                      <span
                          className={
                              isUnpaidCampaignStatus(adData.SubscriptionStatus)
                                  ? 'campaignPaymentBadge unpaid'
                                  : 'campaignPaymentBadge paid'
                          }
                      >
                          {formatAdminCampaignPaymentStatus(adData.SubscriptionStatus)}
                      </span>
                  </div>
                  <div className='campaignSummaryCard'>
                      <span className='summaryLabel'>Storefront</span>
                      <span className='summaryValue'>{adData.Storefront ? 'Enabled' : 'Disabled'}</span>
                  </div>
              </div>

              <div className='campaignDetailsSection'>
                  <h2 className='campaignSectionTitle'>Subscription Window</h2>
                  <div className='campaignDataList'>
                      <div className='campaignDataItem'>
                          <span className='campaignDataLabel'>Start Date</span>
                          <span className='campaignDataValue'>{formatDate(adData.SubscriptionStartDate)}</span>
                      </div>
                      <div className='campaignDataItem'>
                          <span className='campaignDataLabel'>End Date</span>
                          <span className='campaignDataValue'>{formatDate(adData.SubscriptionEndDate)}</span>
                      </div>
                  </div>
              </div>

              <div className='campaignDetailsSection'>
                  <h2 className='campaignSectionTitle'>Campaign Screens</h2>
                  <p className='campaignDataValue'>
                      Current targeting: {getTargetingLabel()}
                      {targetedScreens.length > 0 && (
                          <> · {totalPlaysForScreens(targetedScreens.length).toLocaleString()} guaranteed plays ({PLAYS_PER_SCREEN_MONTH.toLocaleString()} per screen)</>
                      )}
                  </p>
                  {!showEditScreens ? (
                      <button
                          type='button'
                          className='approveBtn userDetailsBtn'
                          onClick={() => {
                              setScreenSaveError('');
                              setEditScreenIds(targetedScreens);
                              setShowEditScreens(true);
                          }}
                          disabled={actionLoading}
                      >
                          Edit screens
                      </button>
                  ) : (
                      <div>
                          <ScreenSelector
                              selectedScreenIds={editScreenIds}
                              onSelectionChange={handleEditScreenSelectionChange}
                              selectionError={screenSaveError}
                          />
                          <div className='newCampaignInputContainer' style={{ marginTop: '1rem' }}>
                              <span className='newCampaignInputTitle'>Monthly fee override (USD)</span>
                              <input
                                  value={editFeeInput}
                                  onChange={(e) => setEditFeeInput(e.target.value)}
                                  type='number'
                                  min='0.01'
                                  step='0.01'
                                  className='newCampaignInputInputFull'
                              />
                          </div>
                          {editScreenIds.length > 0 && (
                              <p className='campaignDataValue'>
                                  Total guaranteed plays: {totalPlaysForScreens(editScreenIds.length).toLocaleString()}
                              </p>
                          )}
                          <div className='campaignActionsRow'>
                              <button
                                  type='button'
                                  className='denyBtnCampaignDetails userDetailsBtn'
                                  onClick={() => {
                                      setShowEditScreens(false);
                                      setScreenSaveError('');
                                      setEditScreenIds(targetedScreens);
                                  }}
                                  disabled={screenSaving}
                              >
                                  Cancel
                              </button>
                              <button
                                  type='button'
                                  className='approveBtn userDetailsBtn'
                                  onClick={handleSaveCampaignScreens}
                                  disabled={screenSaving || editScreenIds.length === 0}
                              >
                                  {screenSaving ? 'Saving…' : 'Save screens'}
                              </button>
                          </div>
                      </div>
                  )}
              </div>

              <div className='campaignDetailsSection'>
                  <h2 className='campaignSectionTitle'>Creative Assets (Live)</h2>
                  <div className='advertismentImagesContainer'>
                      {adData.AdContentHorizontal ? (
                          <CampaignCreativePreview
                              src={adData.AdContentHorizontal}
                              alt='Horizontal campaign asset'
                              slot='horizontal'
                              showLabel
                          />
                      ) : (
                          <div className='creativePlaceholder'>No horizontal asset</div>
                      )}
                      {adData.AdContentVertical ? (
                          <CampaignCreativePreview
                              src={adData.AdContentVertical}
                              alt='Vertical campaign asset'
                              slot='vertical'
                              showLabel
                          />
                      ) : (
                          <div className='creativePlaceholder'>No vertical asset</div>
                      )}
                  </div>
              </div>

              {hasPendingContentRevision() && (
                  <div className='campaignDetailsSection'>
                      <h2 className='campaignSectionTitle'>Pending Creative Revision</h2>
                      <p className='campaignDataValue'>
                          Submitted {formatDate(adData.PendingContentSubmittedAt)} — live creatives remain on screens until you approve.
                      </p>
                      <div className='advertismentImagesContainer'>
                          <div>
                              <CampaignCreativePreview
                                  src={adData.PendingAdContentHorizontal}
                                  alt='Pending horizontal'
                                  slot='horizontal'
                                  showLabel
                              />
                          </div>
                          <div>
                              <CampaignCreativePreview
                                  src={adData.PendingAdContentVertical}
                                  alt='Pending vertical'
                                  slot='vertical'
                                  showLabel
                              />
                          </div>
                      </div>
                      <div className='campaignActionsRow'>
                          <button
                              type='button'
                              className='approveBtn userDetailsBtn'
                              onClick={handleApproveContentRevision}
                              disabled={actionLoading}
                          >
                              Approve content
                          </button>
                          <button
                              type='button'
                              className='denyBtnCampaignDetails userDetailsBtn'
                              onClick={handleRejectContentRevision}
                              disabled={actionLoading}
                          >
                              Reject content
                          </button>
                      </div>
                  </div>
              )}

              <div className='campaignDetailsSection'>
                  <h2 className='campaignSectionTitle'>Edit Live Creatives</h2>
                  <p className='campaignDataValue'>
                      Replace live assets immediately (no review queue). Clears any pending advertiser revision.
                  </p>
                  {!showEditContent ? (
                      <button
                          type='button'
                          className='approveBtn userDetailsBtn'
                          onClick={() => {
                              setContentActionError('');
                              setShowEditContent(true);
                          }}
                          disabled={actionLoading}
                      >
                          Edit creatives
                      </button>
                  ) : (
                      <CampaignContentUploadForm
                          inputIds={ADMIN_CONTENT_INPUT_IDS}
                          creativeHook={adminContentUpload}
                          submitLabel='Save live creatives'
                          submitting={contentSubmitting}
                          error={contentActionError}
                          onSubmit={handleAdminReplaceContent}
                          onCancel={() => {
                              setShowEditContent(false);
                              setContentActionError('');
                              adminContentUpload.resetCreatives();
                          }}
                      />
                  )}
              </div>

              {isUnpaidCampaignStatus(adData.SubscriptionStatus) && (
                  <div className='campaignDetailsSection'>
                      <h2 className='campaignSectionTitle'>Payment reminder</h2>
                      <p className='campaignDataValue'>
                          Send a checkout link so the advertiser can pay and complete this launch campaign.
                      </p>
                      <div className='campaignActionsRow'>
                          <button
                              type='button'
                              className='approveBtn userDetailsBtn'
                              onClick={openReminderModal}
                              disabled={actionLoading}
                          >
                              Send payment reminder
                          </button>
                      </div>
                  </div>
              )}

              {isPendingApprovalCampaign() && (
              <div className='campaignDetailsSection'>
                  <h2 className='campaignSectionTitle'>Approve Campaign</h2>
                  <p className='campaignDataValue'>Campaign ordering is applied automatically when screens request ads.</p>

                  <div className='campaignActionsRow'>
                      <button
                          onClick={() => { approveAdvertisementCall() }}
                          className='approveBtn userDetailsBtn'
                          disabled={actionLoading || isUnpaidCampaignStatus(adData.SubscriptionStatus)}
                          title={
                              isUnpaidCampaignStatus(adData.SubscriptionStatus)
                                  ? 'Cannot approve until the advertiser completes payment'
                                  : ''
                          }
                      >
                          {actionLoading ? 'Approving...' : 'Approve Campaign'}
                      </button>
                  </div>
              </div>
              )}

              <div className='campaignDetailsSection'>
                  <h2 className='campaignSectionTitle'>Campaign Status Management</h2>
                  <div className='campaignActionsRow'>
                      <span className='campaignDataValue'>
                          Current Status: <span className={`campaignStatusBadge status-${adData.StatusId || 0}`}>
                              {getStatusLabel(adData.StatusId)}
                          </span>
                      </span>
                      <button 
                          onClick={toggleCampaignStatus} 
                          className='approveBtn userDetailsBtn' 
                          disabled={actionLoading || adData.StatusId === 3}
                          title={adData.StatusId === 3 ? 'Cannot toggle pending campaigns' : ''}
                      >
                          {actionLoading ? 'Processing...' : adData.StatusId === 1 ? 'Set Inactive' : 'Set Active'}
                      </button>
                  </div>

                  <button className='denyBtnCampaignDetails userDetailsBtn' onClick={()=>{deleteAdvertisement()}} disabled={actionLoading}> 
                      {actionLoading ? 'Processing...' : 'Reject / Delete Campaign'}
                  </button>
              </div>
              {reminderOpen && (
                  <div
                      className='outreachReminderBackdrop'
                      onClick={closeReminderModal}
                      onKeyDown={(e) => e.key === 'Escape' && closeReminderModal()}
                      role='presentation'
                  >
                      <div
                          className='outreachReminderModal'
                          role='dialog'
                          aria-modal='true'
                          aria-label='Send payment reminder'
                          onClick={(e) => e.stopPropagation()}
                      >
                          <div className='outreachReminderModalHeader'>
                              <h2>Send payment reminder</h2>
                              <button type='button' className='outreachReminderClose' onClick={closeReminderModal}>
                                  ×
                              </button>
                          </div>

                          <p className='outreachReminderMeta'>
                              Campaign #{adData.AdvertisementId} · {adData.AdvertisementName}
                          </p>
                          <p className='outreachReminderContact'>
                              Email: {adData.AdvertiserEmail || '—'} · Phone: {adData.AdvertiserPhoneNumber || '—'}
                          </p>

                          <fieldset className='outreachReminderFieldset'>
                              <legend>Channels</legend>
                              <label className='outreachReminderCheckbox'>
                                  <input
                                      type='checkbox'
                                      checked={reminderChannels.email}
                                      onChange={(e) => setReminderChannels((c) => ({ ...c, email: e.target.checked }))}
                                  />
                                  Email
                              </label>
                              <label className='outreachReminderCheckbox'>
                                  <input
                                      type='checkbox'
                                      checked={reminderChannels.sms}
                                      onChange={(e) => setReminderChannels((c) => ({ ...c, sms: e.target.checked }))}
                                  />
                                  SMS
                              </label>
                          </fieldset>

                          <label className='outreachReminderCheckbox outreachReminderCustomToggle'>
                              <input
                                  type='checkbox'
                                  checked={useCustomMessage}
                                  onChange={(e) => setUseCustomMessage(e.target.checked)}
                              />
                              Use custom message
                          </label>

                          {!useCustomMessage && (
                              <label className='outreachReminderLabel'>
                                  Template
                                  <select
                                      className='outreachReminderSelect'
                                      value={selectedTemplateKey}
                                      onChange={(e) => setSelectedTemplateKey(e.target.value)}
                                      disabled={templatesLoading}
                                  >
                                      {getTemplatesForSelectedChannels().map((t) => (
                                          <option key={t.TemplateKey} value={t.TemplateKey}>
                                              {t.Name} ({t.Channel})
                                          </option>
                                      ))}
                                  </select>
                              </label>
                          )}

                          {useCustomMessage && reminderChannels.email && (
                              <>
                                  <label className='outreachReminderLabel'>
                                      Email subject
                                      <input
                                          type='text'
                                          className='outreachReminderInput'
                                          value={customEmailSubject}
                                          onChange={(e) => setCustomEmailSubject(e.target.value)}
                                          placeholder='Complete your INVERT ad payment'
                                      />
                                  </label>
                                  <label className='outreachReminderLabel'>
                                      Email body (HTML allowed)
                                      <textarea
                                          className='outreachReminderTextarea'
                                          rows={5}
                                          value={customEmailHtml}
                                          onChange={(e) => setCustomEmailHtml(e.target.value)}
                                          placeholder='<p>Hi {{firstName}}, … {{continueLink}}</p>'
                                      />
                                  </label>
                              </>
                          )}

                          {useCustomMessage && reminderChannels.sms && (
                              <label className='outreachReminderLabel'>
                                  SMS body
                                  <textarea
                                      className='outreachReminderTextarea'
                                      rows={3}
                                      value={customSmsBody}
                                      onChange={(e) => setCustomSmsBody(e.target.value)}
                                      placeholder='Hi {{firstName}}, finish your INVERT ad … {{continueLink}}'
                                  />
                                  <span className='outreachReminderHint'>Keep under ~300 characters when possible.</span>
                              </label>
                          )}

                          <p className='outreachReminderTags'>
                              Tags: {'{{continueLink}}'}, {'{{firstName}}'}, {'{{companyName}}'}, {'{{campaignName}}'}, {'{{amount}}'}
                          </p>

                          <div className='outreachReminderPreview'>
                              <h3>Preview</h3>
                              {(() => {
                                  const preview = getActiveTemplatePreview();
                                  return (
                                      <>
                                          {reminderChannels.email && preview.emailSubject && (
                                              <p><strong>Email subject:</strong> {preview.emailSubject}</p>
                                          )}
                                          {reminderChannels.email && preview.emailBody && (
                                              <div
                                                  className='outreachReminderPreviewBody'
                                                  dangerouslySetInnerHTML={{ __html: preview.emailBody }}
                                              />
                                          )}
                                          {reminderChannels.sms && preview.smsBody && (
                                              <p><strong>SMS:</strong> {preview.smsBody}</p>
                                          )}
                                      </>
                                  );
                              })()}
                          </div>

                          {reminderSendError && <p className='outreachReminderError'>{reminderSendError}</p>}
                          {reminderSendSuccess && <p className='outreachReminderSuccess'>{reminderSendSuccess}</p>}

                          <div className='outreachReminderActions'>
                              <button type='button' className='denyBtnCampaignDetails userDetailsBtn' onClick={closeReminderModal}>
                                  Cancel
                              </button>
                              <button
                                  type='button'
                                  className='approveBtn userDetailsBtn'
                                  onClick={sendPaymentReminder}
                                  disabled={actionLoading || templatesLoading}
                              >
                                  {actionLoading ? 'Sending…' : 'Send reminder'}
                              </button>
                          </div>
                      </div>
                  </div>
              )}
              </div>
   )
  
}
